// src/pages/Cars.jsx
// Anagrafica AUTO: le auto su cui si montano i dispositivi. In allenamento si
// sceglie per ogni dispositivo l'auto e il pilota (dai Partecipanti); il pilota
// può cambiare durante la sessione. Nome, tipo e dati tecnici finiscono nei
// report PDF.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '../components/icons';
import './Pilots.css';
import './Cars.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const TYPE_HINTS = ['GT Cup', 'GT3', 'GT4', 'Turismo', 'Stradale sportiva', 'Prototipo', 'Formula', 'Kart', 'Rally'];
const EMPTY = { name: '', type: '', number: '', power: '', transmission: '', tyres: '', notes: '', deviceId: '' };

const norm = (s) => String(s || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
const specsOf = (c) => [c.power, c.transmission, c.tyres].filter(Boolean).join(' · ');

function PhotoPicker({ file, currentUrl, onFile, onRemove }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const src = preview || (currentUrl ? `${API_BASE}${currentUrl}` : null);
  return (
    <div
      className={`rs-dropzone pil-dropzone car-photo ${over ? 'is-over' : ''} ${src ? 'pil-dropzone--file' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
    >
      {src ? (
        <div className="pil-dropzone__preview">
          <img src={src} alt="Foto dell'auto" />
          <div className="pil-dropzone__meta">
            <span className="pil-dropzone__name">{file ? file.name : 'Foto attuale'}</span>
            <span className="pil-dropzone__hint">Clicca per sostituire</span>
          </div>
          <button className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon" type="button" title="Togli la foto" aria-label="Togli la foto"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <Icon.X size={14} />
          </button>
        </div>
      ) : (
        <>
          <Icon.Car size={22} />
          <strong>Foto dell'auto</strong>
          <small>Trascina o clicca per caricare · facoltativa</small>
        </>
      )}
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
    </div>
  );
}

export default function CarsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  // modulo: nuova auto (editId null) o modifica di un'auto esistente
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);           // file nuovo
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const formRef = useRef(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    fetch(`${API_BASE}/api/cars`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(j => setList(Array.isArray(j) ? j : []))
      .catch(e => setError(e.message || 'Errore di caricamento'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const openNew = () => {
    setEditId(null); setForm(EMPTY); setPhoto(null); setCurrentPhoto(null); setRemovePhoto(false); setFormError('');
    setFormOpen(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name || '', type: c.type || '', number: c.number || '', power: c.power || '', transmission: c.transmission || '', tyres: c.tyres || '', notes: c.notes || '', deviceId: c.deviceId || '' });
    setPhoto(null); setCurrentPhoto(c.photoUrl || null); setRemovePhoto(false); setFormError('');
    setFormOpen(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const closeForm = () => { setFormOpen(false); setEditId(null); setFormError(''); };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError('Scrivi il nome dell\'auto.'); return; }
    if (!form.type.trim()) { setFormError('Indica il tipo di auto.'); return; }
    setSaving(true);
    setFormError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v).trim()));
      if (photo) fd.append('photo', photo);
      else if (editId && removePhoto) fd.append('removePhoto', '1');
      const r = await fetch(`${API_BASE}/api/cars${editId ? `/${encodeURIComponent(editId)}` : ''}`, { method: editId ? 'PUT' : 'POST', body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setList(prev => (editId ? prev.map(c => (c.id === j.id ? j : c)) : [j, ...prev]));
      setFormOpen(false); setEditId(null); setForm(EMPTY); setPhoto(null); setCurrentPhoto(null);
    } catch (err) {
      setFormError(err.message || 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    setDeletingId(id);
    try {
      const r = await fetch(`${API_BASE}/api/cars/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setList(prev => prev.filter(c => c.id !== id));
      if (editId === id) closeForm();
    } catch (e) {
      alert(`Eliminazione non riuscita: ${e.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = norm(query);
    if (!q) return list;
    return list.filter(c => norm(`${c.name} ${c.type} ${c.number} ${c.power} ${c.transmission} ${c.tyres} ${c.deviceId || ''}`).includes(q));
  }, [list, query]);
  const withDevice = list.filter(c => c.deviceId).length;

  return (
    <div className="rs-page pil-page car-page">
      <header className="rs-page-header">
        <div className="rs-page-header__text">
          <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot" />Gestione</span>
          <h1 className="rs-page-header__title">Auto</h1>
          <p className="rs-page-header__subtitle">Le auto su cui monti i dispositivi. In allenamento scegli per ogni dispositivo l'auto e il pilota; nome, tipo e dati tecnici compaiono nei report PDF.</p>
        </div>
        <div className="rs-page-header__actions">
          <div className="rs-search pil-search">
            <Icon.Search />
            <input type="text" className="rs-input" placeholder="Cerca auto…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Cerca auto" />
          </div>
          <button type="button" className="rs-btn rs-btn--primary" onClick={openNew}>
            <Icon.Plus /> Nuova auto
          </button>
        </div>
      </header>

      <section className="rs-grid rs-grid--4 pil-stats" aria-label="Riepilogo">
        <div className="rs-stat rs-stat--inline"><span className="rs-stat__value">{list.length}</span><span className="rs-stat__label">Auto</span></div>
        <div className="rs-stat rs-stat--inline"><span className="rs-stat__value">{withDevice}</span><span className="rs-stat__label">Con dispositivo fisso</span></div>
      </section>

      {/* MODULO (nuova auto o modifica) */}
      {formOpen && (
        <section className="rs-card pil-form is-open car-form" ref={formRef}>
          <div className="rs-card__head">
            <span className="rs-card__title"><Icon.Car /> {editId ? `Modifica: ${list.find(c => c.id === editId)?.name || 'auto'}` : 'Nuova auto'}</span>
            <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon" onClick={closeForm} aria-label="Chiudi"><Icon.X size={15} /></button>
          </div>
          <form className="pil-form__body" onSubmit={onSubmit}>
            <div className="pil-form__section">
              <div className="pil-form__section-title">Auto</div>
              <div className="rs-grid rs-grid--3">
                <div className="rs-field">
                  <label className="rs-label">Nome auto</label>
                  <input type="text" className="rs-input" value={form.name} onChange={set('name')} placeholder="Porsche 911 GT3 Cup" maxLength={60} required />
                </div>
                <div className="rs-field">
                  <label className="rs-label">Tipo</label>
                  <input type="text" className="rs-input" value={form.type} onChange={set('type')} placeholder="GT Cup, Turismo, Stradale…" maxLength={60} list="car-type-hints" required />
                  <datalist id="car-type-hints">{TYPE_HINTS.map(t => <option key={t} value={t} />)}</datalist>
                </div>
                <div className="rs-field">
                  <label className="rs-label">Numero <span className="rs-label__opt">facoltativo</span></label>
                  <input type="text" className="rs-input" value={form.number} onChange={set('number')} placeholder="es. 7" maxLength={6} />
                  <span className="rs-hint">Sigla sulla mappa live.</span>
                </div>
              </div>
            </div>

            <div className="pil-form__section">
              <div className="pil-form__section-title">Dati tecnici <span className="rs-label__opt">facoltativi · nel report</span></div>
              <div className="rs-grid rs-grid--3">
                <div className="rs-field">
                  <label className="rs-label">Potenza</label>
                  <input type="text" className="rs-input" value={form.power} onChange={set('power')} placeholder="es. 510 CV" maxLength={30} />
                </div>
                <div className="rs-field">
                  <label className="rs-label">Cambio</label>
                  <input type="text" className="rs-input" value={form.transmission} onChange={set('transmission')} placeholder="es. Sequenziale 6 marce" maxLength={60} />
                </div>
                <div className="rs-field">
                  <label className="rs-label">Gomme</label>
                  <input type="text" className="rs-input" value={form.tyres} onChange={set('tyres')} placeholder="es. Michelin Pilot Sport Cup 2" maxLength={60} />
                </div>
              </div>
              <div className="rs-field">
                <label className="rs-label">Note</label>
                <input type="text" className="rs-input" value={form.notes} onChange={set('notes')} placeholder="es. assetto pista, freni maggiorati" maxLength={400} />
              </div>
            </div>

            <div className="pil-form__section">
              <div className="pil-form__section-title">Dispositivo e foto</div>
              <div className="rs-grid rs-grid--2">
                <div className="rs-field">
                  <label className="rs-label">ID dispositivo <span className="rs-label__opt">facoltativo</span></label>
                  <input type="text" inputMode="numeric" className="rs-input" value={form.deviceId} onChange={set('deviceId')} placeholder="vuoto = nessuno" maxLength={12} />
                  <span className="rs-hint">Numero stampato sul dispositivo montato fisso su questa auto: nel setup dell'allenamento l'auto viene proposta da sola.</span>
                </div>
                <div className="rs-field">
                  <label className="rs-label">Foto <span className="rs-label__opt">facoltativa</span></label>
                  <PhotoPicker
                    file={photo}
                    currentUrl={removePhoto ? null : currentPhoto}
                    onFile={(f) => { setPhoto(f); setRemovePhoto(false); }}
                    onRemove={() => { setPhoto(null); setRemovePhoto(true); }}
                  />
                </div>
              </div>
            </div>

            {formError && <div className="rs-alert rs-alert--danger"><Icon.Alert /> {formError}</div>}

            <div className="pil-form__actions">
              <button type="button" className="rs-btn rs-btn--ghost" onClick={closeForm}>Annulla</button>
              <button className="rs-btn rs-btn--primary" type="submit" disabled={saving}>
                {saving ? <><span className="rs-spinner" /> Salvataggio…</> : editId ? <><Icon.Check /> Salva modifiche</> : <><Icon.Plus /> Crea auto</>}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* ELENCO */}
      <section className="rs-card pil-list">
        <div className="rs-card__head">
          <span className="rs-card__title"><Icon.Car /> Elenco</span>
          <div className="pil-list__counts"><span className="rs-chip">Auto <strong>{filtered.length}</strong></span></div>
        </div>

        {loading && <div className="pil-loading"><span className="rs-spinner" /> Caricamento auto…</div>}
        {error && <div className="pil-list__msg"><div className="rs-alert rs-alert--danger"><Icon.Alert /> Errore: {error}</div></div>}

        {!loading && !error && list.length === 0 && (
          <div className="pil-list__msg">
            <div className="rs-empty">
              <Icon.Car />
              <strong>Nessuna auto</strong>
              <p>Crea le auto su cui monti i dispositivi: in allenamento le sceglierai insieme al pilota.</p>
              <button type="button" className="rs-btn rs-btn--sm rs-btn--primary" onClick={openNew}><Icon.Plus /> Nuova auto</button>
            </div>
          </div>
        )}
        {!loading && !error && list.length > 0 && filtered.length === 0 && (
          <div className="pil-list__msg">
            <div className="rs-empty"><Icon.Search /><strong>Nessun risultato</strong><p>Nessuna auto corrisponde alla ricerca.</p></div>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="rs-table-wrap pil-table-wrap">
            <table className="rs-table rs-table--dense pil-table">
              <thead>
                <tr>
                  <th className="pil-col-avatar" aria-label="Foto" />
                  <th>Auto</th>
                  <th>Tipo</th>
                  <th>Dati tecnici</th>
                  <th className="pil-col-device">Dispositivo</th>
                  <th className="car-col-actions" aria-label="Azioni" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <React.Fragment key={c.id}>
                    <tr className={(confirmDelete === c.id || editId === c.id) ? 'is-selected' : ''}>
                      <td className="pil-col-avatar">
                        {c.photoUrl
                          ? <img className="rs-avatar rs-avatar--sm car-thumb" src={`${API_BASE}${c.photoUrl}`} alt={c.name} />
                          : <span className="car-thumb car-thumb--empty"><Icon.Car size={16} /></span>}
                      </td>
                      <td>
                        <span className="pil-name"><strong>{c.name}</strong>{c.number ? <span className="car-number">#{String(c.number).replace(/^#/, '')}</span> : null}</span>
                        {c.notes ? <span className="car-notes" title={c.notes}>{c.notes}</span> : null}
                      </td>
                      <td><span className="rs-badge">{c.type || '—'}</span></td>
                      <td>{specsOf(c) ? <span className="pil-desc" title={specsOf(c)}>{specsOf(c)}</span> : <span className="rs-text-faint">—</span>}</td>
                      <td className="pil-col-device">
                        {c.deviceId
                          ? <span className="rs-chip pil-device" title="Numero del dispositivo montato su questa auto"><Icon.Cpu size={12} />{c.deviceId}</span>
                          : <span className="rs-text-faint">—</span>}
                      </td>
                      <td className="car-col-actions">
                        <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon" title="Modifica auto" aria-label="Modifica auto" onClick={() => openEdit(c)}>
                          <Icon.Edit size={14} />
                        </button>
                        <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon pil-delete" title="Elimina auto" aria-label="Elimina auto"
                          onClick={() => setConfirmDelete(c.id)} disabled={deletingId === c.id}>
                          {deletingId === c.id ? <span className="rs-spinner" /> : <Icon.X size={14} />}
                        </button>
                      </td>
                    </tr>
                    {confirmDelete === c.id && (
                      <tr className="pil-confirm-row">
                        <td colSpan={6}>
                          <div className="rs-confirm pil-confirm">
                            <div className="rs-confirm__text">
                              Eliminare l'auto <strong>{c.name}</strong>? Gli allenamenti già salvati non cambiano.
                            </div>
                            <div className="rs-confirm__actions">
                              <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={() => setConfirmDelete(null)}>Annulla</button>
                              <button type="button" className="rs-btn rs-btn--sm rs-btn--danger" disabled={deletingId === c.id}
                                onClick={() => { setConfirmDelete(null); onDelete(c.id); }}>
                                Elimina
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
